import React from 'react';

import { Box, Icon, OnClick } from '@rbilabs/component-library';
import { FormattedMessage, MessageDescriptor, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import ActionButton, { ActionButtonSizes, ActionLink } from 'components/action-button';
import { Loyalty } from 'components/app-header/loyalty';
import defaultConfig from 'components/app-header/styled';
import Currency from 'components/currency';
import { Drawer } from 'components/drawer';
import LoadingAnimation from 'components/loading-animation';
import VisuallyHidden from 'components/visually-hidden';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import { LOYALTY_ERROR_PLACEHOLDER } from 'hooks/loyalty/constants';
import useRoutes from 'hooks/use-routes';
import { useAuthContext } from 'state/auth';
import { usePaymentContext } from 'state/payment';
import { IPaymentMethod } from 'state/payment/types';
import { useUIContext } from 'state/ui';
import { delayUnmountHOC } from 'utils/delay-unmount-hoc';

const PaddedText = styled.span`
  margin-bottom: 3px;
`;

const BottomText = styled.div`
  font-weight: bold;
`;

interface IRightNav {
  onMyAccountClick: OnClick;
  onReFocus: OnClick;
}

const DelayedUnmountDrawer = delayUnmountHOC(Drawer);

const RightNav: React.FC<IRightNav> = ({ onMyAccountClick, onReFocus }) => {
  const { isAuthenticated } = useAuthContext();
  const { formatMessage } = useIntl();

  const {
    getBalanceFromPaymentMethods,
    paymentMethods,
    loading: paymentLoading,
    hasGetPaymentMethodsError,
  } = usePaymentContext();
  const { loyaltyDetails, loading: loyaltyLoading } = useLoyaltyActiveCard();
  const { showCardTab, showRewardsTab, hideDrawer, isShowingDrawer } = useUIContext();
  const { getLocalizedRouteForPath } = useRoutes();

  let additionalButtons;

  if (isAuthenticated()) {
    if (paymentLoading || loyaltyLoading) {
      additionalButtons = <LoadingAnimation />;
    } else {
      const prepaidPaymentMethod = paymentMethods.find(
        (method: IPaymentMethod) => (method.prepaid as unknown) as boolean
      );
      let CurrencyWithBalance = () => (
        <Currency amount={getBalanceFromPaymentMethods(prepaidPaymentMethod!)} />
      );

      additionalButtons = (
        <>
          <Box margin="0 1rem 0 0">
            <ActionButton
              size={ActionButtonSizes.SMALL}
              variant={defaultConfig.linkVariant}
              transparent
              onClick={showRewardsTab}
              data-testid="my-rewards-link"
              title={formatMessage(messages.myRewards)}
            >
              <PaddedText>{formatMessage(messages.points) + '\n'}</PaddedText>
              <BottomText>
                {loyaltyDetails ? loyaltyDetails.points : LOYALTY_ERROR_PLACEHOLDER}
              </BottomText>
            </ActionButton>
          </Box>
          <Box margin="0 1rem 0 0">
            <ActionButton
              size={ActionButtonSizes.SMALL}
              variant={defaultConfig.linkVariant}
              transparent
              onClick={showCardTab}
              data-testid="tim-card-link"
              title={formatMessage(messages.timCard)}
            >
              <PaddedText>{formatMessage(messages.timCard) + '\n'}</PaddedText>
              {hasGetPaymentMethodsError ? (
                LOYALTY_ERROR_PLACEHOLDER
              ) : (
                <BottomText>
                  <CurrencyWithBalance />
                </BottomText>
              )}
            </ActionButton>
          </Box>
        </>
      );
    }
  }

  if (isAuthenticated()) {
    return (
      <>
        {additionalButtons}
        <DelayedUnmountDrawer onDismiss={hideDrawer} delayTime={500} isVisible={isShowingDrawer}>
          <Loyalty onDismiss={hideDrawer} />
        </DelayedUnmountDrawer>
        <Box margin="0 1rem 0 0">
          <ActionLink
            variant={defaultConfig.linkVariant}
            size={ActionButtonSizes.SMALL}
            transparent
            to={getLocalizedRouteForPath('account')}
            onClick={onMyAccountClick}
            data-testid="my-account-link"
            title={formatMessage(messages.myAccount)}
            onlyIcon
          >
            <Icon icon="profile" color="primary" width="1.5rem" height="1rem" />
            <VisuallyHidden>{formatMessage(messages.myAccount)}</VisuallyHidden>
          </ActionLink>
        </Box>
      </>
    );
  }

  return (
    <Box margin="0 1rem 0 0">
      <ActionButton
        size={ActionButtonSizes.SMALL}
        variant={defaultConfig.buttonVariant}
        to={getLocalizedRouteForPath('signin')}
        onClick={onReFocus}
        data-testid="desktop-sign-in-link"
      >
        <FormattedMessage {...messages.logIn} />
      </ActionButton>
    </Box>
  );
};

export default RightNav;
const messages: Record<string, MessageDescriptor> = defineMessages({
  myAccount: {
    id: 'myAccount',
    defaultMessage: 'My Account',
    description: 'Link for the user to their account page',
  },

  timCard: {
    id: 'timCard',
    defaultMessage: 'Tim Card',
    description: 'Button linking to the users tim card',
  },

  points: {
    id: 'points',
    defaultMessage: 'Points',
    description: 'Text that refers to the users rewards points',
  },

  myRewards: {
    id: 'myRewards',
    defaultMessage: 'My Rewards',
    description: 'Button linking to users rewards',
  },

  logIn: {
    id: 'logIn',
    defaultMessage: 'Log In',
    description: 'Log In header button.',
  },
});
